// VerReporteAsistentas.js
// Panel tabular de solo lectura: reporte diario de asistentas (piso x día),
// con filtros, heatmap por pregunta (quesitos), ranking de cumplimiento y evolución temporal.
// Accesible por: supervisora (sus pisos), admin_asistentas (todos), admin_atcliente
// (solo supervisoras encargadas), admin/admin_servicios/superadmin (todos).
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { ESTRELLAS_INFO, numEstrellas, textoEstrellas } from "../../utils/estrellas";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { todayISO, addDays, lunesISO, C, BG, KpiCard } from "../analisis/comun";
import { getServicioStyle } from "../../utils/servicioColores";
import { useCargaProgresiva, MasFilas } from "./cargaProgresiva";
import PanelFlotante from "../../components/PanelFlotante";
import { ToggleVista, EvolucionCumplimiento, MiniTendencia, CalendarioCumplimiento, EvolucionPreguntas, AtencionSinReportes, statsDias, DetalleSupervisora, EvolucionFranjas, SelectorEmpresas, EMPRESAS_POR_DEFECTO } from "./reporteEvolucion";

// Orden fijo para los mini-donuts de cumplimiento por servicio en la cabecera.
const ORDEN_SERVICIOS = ["PRIME", "TI", "PLUS", "G", "YP", "CTA"];
const RUTA_LABEL = { 1: "R1", 2: "R2", 3: "R3" };
const RUTA_COLOR = { 1: "#0ea5e9", 2: "#f59e0b", 3: "#8b5cf6" };

// ─────────────────────────────────────────────
// Popover de filtro tipo Excel: lista de valores únicos con checkboxes + buscador
// (mismo patrón usado en el Historial de superadmin)
// ─────────────────────────────────────────────
const ColumnFilterPopover = ({ opciones, seleccionados, onChange, renderLabel, renderOption }) => {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [temp, setTemp] = useState(seleccionados);
  const ref = useRef(null);

  useEffect(() => { if (abierto) setTemp(seleccionados); }, [abierto, seleccionados]);

  const cerrar = useCallback(() => setAbierto(false), []);

  const etiqueta = (o) => (renderLabel ? renderLabel(o) : (o === "" ? "(vacío)" : String(o)));

  const filtradas = busqueda.trim()
    ? opciones.filter((o) => etiqueta(o).toLowerCase().includes(busqueda.trim().toLowerCase()))
    : opciones;

  const toggle = (v) => setTemp((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  const activo = seleccionados.length > 0;

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block", marginLeft: 5 }}>
      <button
        onClick={(e) => { e.stopPropagation(); setAbierto((a) => !a); }}
        title="Filtrar"
        style={{
          border: "none", background: activo ? "#4338ca" : "#e2e8f0",
          color: activo ? "#fff" : "#64748b", borderRadius: 5, cursor: "pointer",
          fontSize: 10, padding: "2px 6px", lineHeight: 1.4, fontWeight: 700,
        }}
      >▾</button>
      {abierto && (
        <PanelFlotante anchorRef={ref} onCerrar={cerrar} style={{
          background: "#fff",
          border: "1.5px solid #e2e8f0", borderRadius: 12, boxShadow: "0 16px 36px rgba(15,23,42,0.25)",
          width: 220, padding: 10, textAlign: "left", color: "#334155", textTransform: "none",
        }}>
          <input
            value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="🔍 Buscar…"
            style={{ width: "100%", padding: "6px 8px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12.5, marginBottom: 6, boxSizing: "border-box", outline: "none" }}
          />
          <div style={{ display: "flex", gap: 10, marginBottom: 6, fontSize: 11 }}>
            <span onClick={() => setTemp(filtradas)} style={{ color: "#4338ca", cursor: "pointer", fontWeight: 700 }}>Marcar todos</span>
            <span onClick={() => setTemp([])} style={{ color: "#94a3b8", cursor: "pointer", fontWeight: 700 }}>Desmarcar</span>
          </div>
          <div style={{ maxHeight: 190, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
            {filtradas.length === 0 && <div style={{ fontSize: 12, color: "#94a3b8", padding: 4 }}>Sin valores</div>}
            {filtradas.map((o) => (
              <label key={o} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, cursor: "pointer", padding: "3px 2px" }}>
                <input type="checkbox" checked={temp.includes(o)} onChange={() => toggle(o)} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{renderOption ? renderOption(o) : etiqueta(o)}</span>
              </label>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <button onClick={() => { onChange([]); setAbierto(false); }} style={{ flex: 1, padding: "6px 0", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Limpiar</button>
            <button onClick={() => { onChange(temp); setAbierto(false); }} style={{ flex: 1, padding: "6px 0", borderRadius: 8, border: "none", background: "#4338ca", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Aplicar</button>
          </div>
        </PanelFlotante>
      )}
    </span>
  );
};

const TIEMPO_FILTRO_VACIO = {
  totalMin: "", totalMax: "", medioMin: "", medioMax: "",
  horaDesde: "", horaHasta: "", enHorario: "todos",
};

// ─────────────────────────────────────────────
// Popover de filtro por rango de tiempo (total en minutos, medio por pregunta
// en segundos), por franja horaria de envío (hh:mm) y por si se envió dentro
// o fuera del horario de la asistenta — mismo aspecto que ColumnFilterPopover
// pero con inputs numéricos/hora en vez de una lista de valores.
// ─────────────────────────────────────────────
const RangoTiempoPopover = ({ filtro, onChange, conEnHorario }) => {
  const [abierto, setAbierto] = useState(false);
  const [temp, setTemp] = useState(filtro);
  const ref = useRef(null);

  useEffect(() => { if (abierto) setTemp(filtro); }, [abierto, filtro]);

  const cerrar = useCallback(() => setAbierto(false), []);

  const activo = Object.entries(filtro).some(([k, v]) => (k === "enHorario" ? v !== "todos" : v !== ""));
  const inputStyle = { width: "50%", padding: "10px 12px", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: 15, boxSizing: "border-box" };
  const etiquetaStyle = { fontSize: 13, fontWeight: 800, color: "#475569", marginBottom: 6 };

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block", marginLeft: 5 }}>
      <button
        onClick={(e) => { e.stopPropagation(); setAbierto((a) => !a); }}
        title="Filtrar por tiempo"
        style={{
          border: "none", background: activo ? "#4338ca" : "#e2e8f0",
          color: activo ? "#fff" : "#64748b", borderRadius: 5, cursor: "pointer",
          fontSize: 10, padding: "2px 6px", lineHeight: 1.4, fontWeight: 700,
        }}
      >▾</button>
      {abierto && (
        <PanelFlotante anchorRef={ref} onCerrar={cerrar} style={{
          background: "#fff",
          border: "1.5px solid #e2e8f0", borderRadius: 14, boxShadow: "0 20px 44px rgba(15,23,42,0.3)",
          width: 300, padding: 16, textAlign: "left", color: "#334155", textTransform: "none",
        }}>
          <div style={etiquetaStyle}>⏱ Tiempo total (min)</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <input type="number" min="0" placeholder="Mín" value={temp.totalMin} onChange={(e) => setTemp((t) => ({ ...t, totalMin: e.target.value }))} style={inputStyle} />
            <input type="number" min="0" placeholder="Máx" value={temp.totalMax} onChange={(e) => setTemp((t) => ({ ...t, totalMax: e.target.value }))} style={inputStyle} />
          </div>
          <div style={etiquetaStyle}>⏱ Tiempo medio por pregunta (seg)</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <input type="number" min="0" placeholder="Mín" value={temp.medioMin} onChange={(e) => setTemp((t) => ({ ...t, medioMin: e.target.value }))} style={inputStyle} />
            <input type="number" min="0" placeholder="Máx" value={temp.medioMax} onChange={(e) => setTemp((t) => ({ ...t, medioMax: e.target.value }))} style={inputStyle} />
          </div>
          <div style={etiquetaStyle}>🕐 Hora de envío (hh:mm inicio–final)</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <input type="time" value={temp.horaDesde} onChange={(e) => setTemp((t) => ({ ...t, horaDesde: e.target.value }))} style={inputStyle} />
            <input type="time" value={temp.horaHasta} onChange={(e) => setTemp((t) => ({ ...t, horaHasta: e.target.value }))} style={inputStyle} />
          </div>
          {conEnHorario && (
            <>
              <div style={etiquetaStyle}>📍 Respecto al horario de la asistenta</div>
              <select value={temp.enHorario} onChange={(e) => setTemp((t) => ({ ...t, enHorario: e.target.value }))}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: 15, boxSizing: "border-box", marginBottom: 14 }}>
                <option value="todos">Todos</option>
                <option value="dentro">✅ Dentro de horario</option>
                <option value="margen">🟠 Hasta 30 min tras la salida</option>
                <option value="fuera">🔴 Fuera de horario</option>
              </select>
            </>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button onClick={() => { onChange(TIEMPO_FILTRO_VACIO); setAbierto(false); }} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "1.5px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Limpiar</button>
            <button onClick={() => { onChange(temp); setAbierto(false); }} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "none", background: "#4338ca", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Aplicar</button>
          </div>
        </PanelFlotante>
      )}
    </span>
  );
};

// ─────────────────────────────────────────────
// Selector de categoría (chip grande desplegable, en vez de un <select> nativo)
// ─────────────────────────────────────────────
const CategoriaSelector = ({ categorias, value, onChange }) => {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => { document.removeEventListener("mousedown", handler); document.removeEventListener("touchstart", handler); };
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setAbierto((a) => !a)}
        disabled={categorias.length === 0}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          padding: "16px 20px", borderRadius: 18, border: "none",
          background: "linear-gradient(135deg,#6366f1,#4338ca)", color: "#fff",
          cursor: categorias.length ? "pointer" : "default",
          boxShadow: abierto ? "0 4px 14px rgba(67,56,202,0.45)" : "0 6px 20px rgba(79,70,229,0.32)",
          fontWeight: 800, fontSize: 17, letterSpacing: 0.2, transition: "box-shadow .15s",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 22 }}>🗂️</span>
          {categorias.length === 0 ? "Sin categorías activas" : value || "Todas las categorías"}
        </span>
        <span style={{ fontSize: 13, opacity: 0.85, transform: abierto ? "rotate(180deg)" : "none", transition: "transform .18s" }}>▾</span>
      </button>
      {abierto && categorias.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, zIndex: 60,
          background: "#fff", borderRadius: 16, border: "1.5px solid #e0e7ff",
          boxShadow: "0 16px 40px rgba(30,27,75,0.22)", padding: 8,
          display: "flex", flexDirection: "column", gap: 3, maxHeight: 280, overflowY: "auto",
        }}>
          {["", ...categorias].map((c) => (
            <div
              key={c || "__todas"}
              onClick={() => { onChange(c); setAbierto(false); }}
              style={{
                padding: "12px 16px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 15,
                display: "flex", alignItems: "center", gap: 8,
                background: c === value ? "linear-gradient(135deg,#eef2ff,#e0e7ff)" : "transparent",
                color: c === value ? "#4338ca" : "#334155",
              }}
            >
              <span>{c === value ? "✅" : "🗂️"}</span> {c || "Todas las categorías"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Render de respuestas (coherente con VerReporteDiario.js)
// ─────────────────────────────────────────────
const SEMAFORO_INFO = {
  verde: { label: "Bien", bg: "#16a34a", emoji: "🟢" },
  amarillo: { label: "Regular", bg: "#d97706", emoji: "🟡" },
  rojo: { label: "Mal", bg: "#dc2626", emoji: "🔴" },
  desconozco: { label: "?", bg: "#94a3b8", emoji: "❓" },
};
const SI_NO_INFO = {
  si: { label: "Sí", bg: "#16a34a", emoji: "✅" },
  no: { label: "No", bg: "#dc2626", emoji: "❌" },
};

const CeldaRespuesta = ({ pregunta, valor }) => {
  if (valor === undefined || valor === null || valor === "") {
    return <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>;
  }
  if (pregunta.tipo === "semaforo" || pregunta.tipo === "semaforo+desconozco") {
    const info = SEMAFORO_INFO[valor];
    if (!info) return <span style={{ fontSize: 12 }}>{valor}</span>;
    return <span style={{ ...st.badge, background: info.bg }}>{info.emoji} {info.label}</span>;
  }
  if (pregunta.tipo === "estrellas") {
    const n = numEstrellas(valor);
    if (n === null) return <span style={{ fontSize: 12 }}>{String(valor)}</span>;
    return <span title={`${n}/5 · ${ESTRELLAS_INFO[n].label}`} style={{ ...st.badge, background: ESTRELLAS_INFO[n].bg }}>★ {n}</span>;
  }
  if (pregunta.tipo === "si_no") {
    const info = SI_NO_INFO[valor];
    if (!info) return <span style={{ fontSize: 12 }}>{valor}</span>;
    return <span style={{ ...st.badge, background: info.bg }}>{info.emoji} {info.label}</span>;
  }
  if (pregunta.tipo === "opciones_color") {
    // respuesta múltiple: array de ids
    const ids = Array.isArray(valor) ? valor : [valor];
    return (
      <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 3, justifyContent: "center" }}>
        {ids.map((id) => {
          const op = (pregunta.valores || []).find((v) => String(v.id) === String(id));
          if (!op) return <span key={id} style={{ fontSize: 12 }}>{id}</span>;
          return <span key={id} style={{ ...st.badge, background: op.color || "#3b82f6" }}>{op.etiqueta}</span>;
        })}
      </span>
    );
  }
  return <span style={{ fontSize: 12, whiteSpace: "pre-wrap", maxWidth: 160, display: "inline-block" }} title={String(valor)}>{String(valor).slice(0, 40)}{String(valor).length > 40 ? "…" : ""}</span>;
};

// Etiqueta legible de un valor de respuesta, para el filtro tipo Excel de cada pregunta
const etiquetaValorPregunta = (pregunta, valor) => {
  if (valor === "") return "— Sin respuesta —";
  if (pregunta.tipo === "semaforo" || pregunta.tipo === "semaforo+desconozco") {
    const info = SEMAFORO_INFO[valor];
    return info ? `${info.emoji} ${info.label}` : String(valor);
  }
  if (pregunta.tipo === "estrellas") {
    const n = numEstrellas(valor);
    return n === null ? String(valor) : `${textoEstrellas(n)} ${n}/5`;
  }
  if (pregunta.tipo === "si_no") {
    const info = SI_NO_INFO[valor];
    return info ? `${info.emoji} ${info.label}` : String(valor);
  }
  if (pregunta.tipo === "opciones_color") {
    const op = (pregunta.valores || []).find((v) => String(v.id) === String(valor));
    return op ? op.etiqueta : String(valor);
  }
  return String(valor);
};

// Barra horizontal de cumplimiento (reportes enviados / total esperado)
const BarraCumplimiento = ({ label, enviados, total, pct }) => {
  const color = pct == null ? "#94a3b8" : pct >= 80 ? C.verde : pct >= 50 ? C.amarillo : C.rojo;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, fontWeight: 700, color: "#374151", marginBottom: 3 }}>
        <span style={{ textTransform: "capitalize" }}>{label}</span>
        <span style={{ color, fontWeight: 800 }}>{enviados}/{total} · {pct != null ? `${pct}%` : "—"}</span>
      </div>
      <div style={{ display: "flex", height: 10, borderRadius: 6, overflow: "hidden", background: "#f3f4f6" }}>
        <div style={{ width: `${pct || 0}%`, background: color, transition: "width .3s" }} />
      </div>
    </div>
  );
};

// Quesito (gráfico circular) vía conic-gradient, sin dependencias externas
const PieChart = ({ segmentos, size = 84 }) => {
  const total = segmentos.reduce((s, x) => s + x.valor, 0);
  if (!total) return null;
  let acc = 0;
  const stops = segmentos.filter((s) => s.valor > 0).map((s) => {
    const desde = (acc / total) * 100;
    acc += s.valor;
    const hasta = (acc / total) * 100;
    return `${s.color} ${desde}% ${hasta}%`;
  }).join(", ");
  return <div style={{ width: size, height: size, borderRadius: "50%", background: `conic-gradient(${stops})`, flexShrink: 0, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }} />;
};

// Anillo de cumplimiento con los números (enviados/total) en el centro, en vez de
// un porcentaje. Vive debajo de los filtros de período; mientras carga muestra
// un anillo neutro con "…".
const DonutNumeros = ({ enviados, total, size = 70, loading, compacto = false }) => {
  const pct = total ? Math.round((enviados / total) * 100) : null;
  const color = loading ? "#94a3b8" : pct == null ? "#94a3b8" : pct >= 80 ? C.verde : pct >= 50 ? C.amarillo : C.rojo;
  const r = (size - 9) / 2, cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const dash = !loading && pct != null ? Math.max(0, Math.min(1, pct / 100)) * circ : 0;
  // hueco útil dentro del anillo y tamaño de letra que cabe según las cifras
  const hueco = 2 * r - (compacto ? 5 : 8) - 8;
  const ajustar = (texto, base) => Math.max(7, Math.min(base, hueco / (String(texto).length * 0.6)));
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={compacto ? 5 : 8} />
      {dash > 0 && (
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={compacto ? 5 : 8}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`} style={{ transition: "stroke-dasharray 0.6s" }} />
      )}
      {loading ? (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize={Math.max(9, size * 0.24)} fontWeight={800} fill="#94a3b8">…</text>
      ) : compacto && `${enviados}/${total}`.length <= 5 ? (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize={ajustar(`${enviados}/${total}`, size * 0.24)} fontWeight={800} fill="#1e293b">
          {enviados}/{total}
        </text>
      ) : (
        // dos líneas (enviados arriba, /total abajo) y letra que se encoge con
        // el número de cifras para no salirse nunca del anillo
        <>
          <text x={cx} y={cy - size * 0.08} textAnchor="middle" dominantBaseline="middle" fontSize={ajustar(String(enviados), size * (compacto ? 0.24 : 0.27))} fontWeight={800} fill="#1e293b">
            {enviados}
          </text>
          <text x={cx} y={cy + size * 0.17} textAnchor="middle" dominantBaseline="middle" fontSize={ajustar(`/${total}`, size * (compacto ? 0.16 : 0.15))} fontWeight={700} fill="#94a3b8">
            /{total}
          </text>
        </>
      )}
    </svg>
  );
};

// Fila de heatmap: quesito + leyenda, calculando los segmentos según el tipo de pregunta
const HeatmapPregunta = ({ pregunta, filas }) => {
  const id = String(pregunta.id);
  let segmentos = [];
  let total = 0;

  if (pregunta.tipo === "semaforo" || pregunta.tipo === "semaforo+desconozco") {
    const c = contarPregunta(filas, id);
    segmentos = [
      { label: "Bien", valor: c.verde, color: C.verde },
      { label: "Regular", valor: c.amarillo, color: C.amarillo },
      { label: "Mal", valor: c.rojo, color: C.rojo },
      { label: "Desconoce", valor: c.desconozco, color: "#94a3b8" },
    ];
    total = c.total;
  } else if (pregunta.tipo === "estrellas") {
    const conteos = [0, 0, 0, 0, 0, 0];
    filas.forEach((f) => {
      if (!f.enviado) return;
      const resumenAlumnos = f.respuestas_alumnos?.[id];
      if (resumenAlumnos) {
        Object.entries(resumenAlumnos).forEach(([v, nombres]) => { const n = numEstrellas(v); if (n !== null) { conteos[n] += (nombres || []).length; total += (nombres || []).length; } });
      } else {
        const n = numEstrellas(f.respuestas?.[id]);
        if (n !== null) { conteos[n]++; total++; }
      }
    });
    segmentos = [1, 2, 3, 4, 5].map((n) => ({ label: `${n} ★`, valor: conteos[n], color: ESTRELLAS_INFO[n].bg }));
  } else if (pregunta.tipo === "si_no") {
    const c = contarPregunta(filas, id);
    segmentos = [{ label: "Sí", valor: c.verde, color: C.verde }, { label: "No", valor: c.rojo, color: C.rojo }];
    total = c.total;
  } else if (pregunta.tipo === "opciones_color") {
    const conteos = new Map();
    filas.forEach((f) => {
      if (!f.enviado) return;
      const v = f.respuestas?.[id];
      if (v === undefined || v === null || v === "") return;
      // respuesta múltiple: cuenta cada opción marcada
      (Array.isArray(v) ? v : [v]).forEach((x) => { conteos.set(String(x), (conteos.get(String(x)) || 0) + 1); total++; });
    });
    segmentos = (pregunta.valores || []).map((op) => ({ label: op.etiqueta, valor: conteos.get(String(op.id)) || 0, color: op.color || "#3b82f6" }));
  } else {
    let respondidas = 0;
    filas.forEach((f) => {
      if (!f.enviado) return;
      total++;
      const v = f.respuestas?.[id];
      if (v !== undefined && v !== null && v !== "") respondidas++;
    });
    segmentos = [{ label: "Respondida", valor: respondidas, color: C.azul }, { label: "Sin responder", valor: total - respondidas, color: "#e2e8f0" }];
  }

  if (!total) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid #f1f5f9" }}>
      <PieChart segmentos={segmentos} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 4 }}>{pregunta.emoji} {pregunta.subcategoria}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {segmentos.filter((s) => s.valor > 0).map((s) => (
            <span key={s.label} style={{ fontSize: 11.5, color: "#475569", display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: s.color, display: "inline-block" }} />
              {s.label} {s.valor} ({Math.round((s.valor / total) * 100)}%)
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// Tarjeta de ranking genérica (top N con un badge a la derecha definido por `render`).
// Si `todos` trae más elementos que `items`, se puede abrir un modal con el listado completo.
const RankingLista = ({ titulo, emoji, items, todos, vacio, render, mostrarSupervisora }) => {
  const [verTodas, setVerTodas] = useState(false);
  const listaCompleta = todos || items;
  const hayMas = listaCompleta.length > items.length;

  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0", flex: "1 1 320px", minWidth: 280 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
        <h3 style={{ ...st.sh, marginBottom: 0 }}>{emoji} {titulo}</h3>
        {hayMas && (
          <span onClick={() => setVerTodas(true)} style={{ fontSize: 12, fontWeight: 700, color: "#4338ca", cursor: "pointer", whiteSpace: "nowrap" }}>
            Ver todas ({listaCompleta.length}) →
          </span>
        )}
      </div>
      {items.length === 0 ? (
        <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 8 }}>{vacio}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
          {items.map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 4px", borderBottom: i < items.length - 1 ? "1px solid #f1f5f9" : "none" }}>
              <span style={{ width: 20, textAlign: "center", fontWeight: 800, color: "#94a3b8", fontSize: 12.5 }}>{i + 1}</span>
              <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: "#1e293b" }}>
                {item.nombre}
                {mostrarSupervisora && item.supervisora && item.supervisora !== "—" && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8" }}> ({item.supervisora})</span>
                )}
              </span>
              {render(item)}
            </div>
          ))}
        </div>
      )}

      {verTodas && (
        <div onClick={() => setVerTodas(false)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, padding: "18px 20px", width: "100%", maxWidth: 460, maxHeight: "78vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 50px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: "#1e293b" }}>{emoji} {titulo}</span>
              <button onClick={() => setVerTodas(false)} style={{ background: "#f3f4f6", border: "none", borderRadius: 10, padding: "6px 12px", cursor: "pointer", fontSize: 14, color: "#6b7280", fontWeight: 700 }}>✕</button>
            </div>
            <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
              {listaCompleta.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", borderBottom: i < listaCompleta.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                  <span style={{ width: 24, textAlign: "center", fontWeight: 800, color: "#94a3b8", fontSize: 12.5 }}>{i + 1}</span>
                  <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: "#1e293b" }}>
                    {item.nombre}
                    {mostrarSupervisora && item.supervisora && item.supervisora !== "—" && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8" }}> ({item.supervisora})</span>
                    )}
                  </span>
                  {render(item)}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



// ─────────────────────────────────────────────
// Períodos rápidos (granularidad diaria, distinto del selector estándar de Analisis)
// ─────────────────────────────────────────────
const PERIODOS_DIA = [
  { key: "hoy", label: "Hoy" },
  { key: "ayer", label: "Ayer" },
  { key: "semana", label: "Esta semana" },
  { key: "sem_ant", label: "Sem. anterior" },
  { key: "custom", label: "Personalizado" },
];
function rangoDePeriodo(key, custom) {
  const hoy = todayISO(), lun = lunesISO();
  if (key === "hoy") return { desde: hoy, hasta: hoy };
  if (key === "ayer") { const a = addDays(hoy, -1); return { desde: a, hasta: a }; }
  if (key === "semana") return { desde: lun, hasta: hoy };
  if (key === "sem_ant") return { desde: addDays(lun, -7), hasta: addDays(lun, -1) };
  return custom;
}

// Cuenta verde/amarillo/rojo/desconozco (si_no se mapea a verde/rojo) de un conjunto de filas para una pregunta
function contarPregunta(filas, idPregunta) {
  const c = { verde: 0, amarillo: 0, rojo: 0, desconozco: 0, total: 0 };
  filas.forEach((f) => {
    if (!f.enviado) return;
    const v = f.respuestas?.[idPregunta];
    if (v === undefined || v === null || v === "") return;
    c.total++;
    if (v === "si") c.verde++;
    else if (v === "no") c.rojo++;
    else if (v === "verde" || v === "amarillo" || v === "rojo" || v === "desconozco") c[v]++;
  });
  return c;
}

// Mismo criterio de color que los quesitos de cumplimiento (≥80 verde, ≥50 ámbar, si no rojo).
function colorPorPct(pct) {
  if (pct == null) return { color: C.gris, background: BG.gris };
  if (pct >= 80) return { color: C.verde, background: BG.verde };
  if (pct >= 50) return { color: C.amarillo, background: BG.amarillo };
  return { color: C.rojo, background: BG.rojo };
}

const formatHora = (isoString) => {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (isNaN(d)) return null;
  return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
};

const formatDuracion = (segundos) => {
  if (segundos == null) return null;
  const total = Math.round(segundos);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

// Insignia estilo "reloj digital": dígitos monoespaciados en una pastilla
// clara (sin fondo oscuro). "variante" permite pintarla en verde/rojo (p.ej.
// para marcar si el reporte se envió el día que tocaba o no).
const RELOJ_ESTILOS = {
  neutro: { background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0" },
  verde: { background: "#dcfce7", color: "#15803d", border: "1px solid #86efac" },
  rojo: { background: "#fee2e2", color: "#b91c1c", border: "1px solid #fca5a5" },
  naranja: { background: "#ffedd5", color: "#c2410c", border: "1px solid #fdba74" },
};

// ── Respeto de horarios ──────────────────────────────────────────────────
// Tres franjas según la hora de envío respecto al horario de la asistenta:
//   en horario (entre entrada y salida), hasta 30 min tras la salida, y fuera
//   de horario (antes de la entrada o más de 30 min después de la salida).
const MARGEN_SALIDA_MIN = 30;
const aMinutos = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const franjaHorario = (f) => {
  if (!f.enviado || !f.fecha_hora) return null;
  const envio = aMinutos(String(f.fecha_hora).slice(11, 16));
  const entrada = aMinutos(f.hora_entrada), salida = aMinutos(f.hora_salida);
  if (envio == null || entrada == null || salida == null) return null;
  if (envio < entrada) return "fuera";
  if (envio <= salida) return "dentro";
  return envio - salida <= MARGEN_SALIDA_MIN ? "margen" : "fuera";
};
const FRANJAS_HORARIO = [
  { k: "dentro", label: "En horario", icono: "✅", color: "#16a34a" },
  { k: "margen", label: `Hasta ${MARGEN_SALIDA_MIN} min tras la salida`, icono: "🟠", color: "#ea580c" },
  { k: "fuera", label: "Fuera de horario", icono: "🔴", color: "#dc2626" },
];
const contarFranjas = (filas) => {
  const c = { dentro: 0, margen: 0, fuera: 0 };
  filas.forEach((f) => { const fr = franjaHorario(f); if (fr) c[fr]++; });
  return { ...c, total: c.dentro + c.margen + c.fuera };
};

// Barra 100 % apilada con las tres franjas (con número y % en cada trozo)
const BarraFranjas = ({ c, alto = 22, etiquetas = true }) => {
  if (!c.total) return <div style={{ fontSize: 12, color: "#94a3b8" }}>Sin reportes con horario para comparar.</div>;
  return (
    <div style={{ display: "flex", gap: 2, height: alto, borderRadius: 6, overflow: "hidden" }}>
      {FRANJAS_HORARIO.map((fr) => {
        const n = c[fr.k]; if (!n) return null;
        const pct = Math.round((n / c.total) * 100);
        return (
          <div key={fr.k} title={`${fr.label}: ${n} (${pct}%)`} style={{
            flex: n, background: fr.color, color: "#fff", fontSize: 11.5, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center", minWidth: 4, whiteSpace: "nowrap", overflow: "hidden",
          }}>{etiquetas && pct >= 9 ? `${pct}%` : ""}</div>
        );
      })}
    </div>
  );
};

// Quien TENÍA que ir ese día: la sustituta si hay una sustitución aplicada en vigor
// (sustituciones_aplicadas), si no la titular del piso.
const nombreAsistenta = (f) => f.nombre_responsable || f.nombre_asistenta || (f.id_asistenta ? `Asistenta ${f.id_asistenta}` : null);
// "🔄 María López" + "sustituye a Ana Pérez" (y si no hay reporte, a quién le tocaba)
const InfoSustitucion = ({ f }) => {
  const s = f.sustitucion;
  if (!s) return null;
  return (
    <div style={{ fontSize: 11, color: "#7e22ce", fontWeight: 600, marginTop: 2, whiteSpace: "normal" }}>
      {f.enviado && !f.sustituta
        ? <>sustituye a {s.nombre_titular}{s.horario_propio ? " · horario de la sustitución" : ""}</>
        : <>tocaba a 🔄 {s.nombre_sustituta} (sustituta de {s.nombre_titular})</>}
    </div>
  );
};
const AGRUPACIONES_RESPETO = [{ clave: "supervisora", texto: "supervisora", valor: (f) => f.user_supervisora, sub: { texto: "asistenta", valor: nombreAsistenta } }, { clave: "asistenta", texto: "asistenta", valor: nombreAsistenta, filtroSupervisora: true }];

// Botón desplegable (píldora) a juego con los selectores índigo del reporte
const BotonDesplegable = ({ abierto, onClick, texto, n }) => (
  <button onClick={onClick} style={{
    display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 14px", borderRadius: 999,
    border: "2px solid #c7d2fe", background: abierto ? "#eef2ff" : "linear-gradient(135deg,#fff,#eef2ff)",
    color: "#3730a3", fontWeight: 800, fontSize: 12.5, cursor: "pointer",
    boxShadow: abierto ? "none" : "0 2px 8px rgba(79,70,229,0.15)", transition: "all .15s",
  }}>
    <span style={{ display: "inline-block", transition: "transform .2s", transform: abierto ? "rotate(180deg)" : "none" }}>▾</span>
    {texto}
    {n != null && <span style={{ background: "#4338ca", color: "#fff", borderRadius: 999, padding: "1px 8px", fontSize: 11 }}>{n}</span>}
  </button>
);

// agrupaciones: [{ clave, texto, valor: (fila) => nombre }] — un desplegable por cada una
const RespetoHorarios = ({ filas, agrupaciones }) => {
  const [abierta, setAbierta] = React.useState(null);
  const [itemAbierto, setItemAbierto] = React.useState(null);
  const [verEvolucion, setVerEvolucion] = React.useState(false);
  const [filtroSup, setFiltroSup] = React.useState("todas");
  const c = React.useMemo(() => contarFranjas(filas), [filas]);
  const agrupar = (ag, fuente) => {
    const m = new Map();
    fuente.forEach((f) => { const k = ag.valor(f) || "—"; if (!m.has(k)) m.set(k, []); m.get(k).push(f); });
    return Array.from(m.entries()).map(([nombre, fs]) => ({ nombre, fs, c: contarFranjas(fs) }))
      .filter((x) => x.c.total > 0)
      .sort((a, b) => (b.c.fuera / b.c.total) - (a.c.fuera / a.c.total) || (b.c.margen / b.c.total) - (a.c.margen / a.c.total));
  };
  const grupos = React.useMemo(() => agrupaciones.map((ag) => ({ ...ag, items: agrupar(ag, filas) })), [filas, agrupaciones]); // eslint-disable-line react-hooks/exhaustive-deps
  const supervisorasResp = React.useMemo(() => [...new Set(filas.filter((f) => franjaHorario(f)).map((f) => f.user_supervisora || "—"))].sort(), [filas]);
  const variosDias = React.useMemo(() => new Set(filas.filter((f) => franjaHorario(f)).map((f) => f.fecha)).size > 1, [filas]);
  const visibles = grupos.filter((g) => g.items.length > 1);
  const grupoAbierto = visibles.find((g) => g.clave === abierta);
  const conFiltroSup = grupoAbierto?.filtroSupervisora && supervisorasResp.length > 1;
  const actual = grupoAbierto && conFiltroSup && filtroSup !== "todas"
    ? { ...grupoAbierto, items: agrupar(grupoAbierto, filas.filter((f) => (f.user_supervisora || "—") === filtroSup)) }
    : grupoAbierto;
  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
      <h3 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 800, color: "#1e293b" }}>⏰ Respeto de horarios</h3>
      <BarraFranjas c={c} alto={26} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 10 }}>
        {FRANJAS_HORARIO.map((fr) => (
          <div key={fr.k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#334155" }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: fr.color }} />
            <span>{fr.icono} {fr.label}</span>
            <strong>{c[fr.k]}</strong>
            <span style={{ color: "#94a3b8" }}>({c.total ? Math.round((c[fr.k] / c.total) * 100) : 0}%)</span>
          </div>
        ))}
      </div>
      {(visibles.length > 0 || variosDias) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
          {variosDias && (
            <BotonDesplegable abierto={verEvolucion} texto="📈 Ver evolución" onClick={() => setVerEvolucion((v) => !v)} />
          )}
          {visibles.map((g) => (
            <BotonDesplegable key={g.clave} abierto={abierta === g.clave} n={g.items.length}
              texto={`Ver por ${g.texto}`} onClick={() => { setItemAbierto(null); setAbierta((a) => (a === g.clave ? null : g.clave)); }} />
          ))}
        </div>
      )}
      {verEvolucion && variosDias && (
        <div style={{ marginTop: 12, padding: "12px 14px", background: "#f8fafc", border: "1px solid #e0e7ff", borderRadius: 12 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: "#475569", marginBottom: 8 }}>📈 Evolución del respeto de horarios <span style={{ fontWeight: 500, color: "#94a3b8" }}>(pasa el ratón por cada columna)</span></div>
          <EvolucionFranjas filas={filas} franja={franjaHorario} franjas={FRANJAS_HORARIO} alto={220} />
        </div>
      )}
      {actual && conFiltroSup && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: "#64748b" }}>👩‍💼 Supervisora:</span>
          <select value={filtroSup} onChange={(e) => { setFiltroSup(e.target.value); setItemAbierto(null); }} style={{
            padding: "7px 12px", borderRadius: 12, border: "2px solid #c7d2fe", fontSize: 12.5,
            background: "linear-gradient(135deg,#fff,#eef2ff)", fontWeight: 800, color: "#3730a3",
            cursor: "pointer", boxShadow: "0 2px 8px rgba(79,70,229,0.15)", outline: "none",
          }}>
            <option value="todas">Todas las supervisoras</option>
            {supervisorasResp.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}
      {actual && actual.items.length === 0 && (
        <div style={{ fontSize: 12.5, color: "#94a3b8", marginTop: 10 }}>Sin reportes con horario para esta supervisora.</div>
      )}
      {actual && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12, maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
          {actual.items.map(({ nombre, fs, c: cs }) => {
            const abiertoItem = itemAbierto === nombre;
            const subs = abiertoItem && actual.sub ? (() => {
              const m = new Map();
              fs.forEach((f) => { const k = actual.sub.valor(f) || "—"; if (!m.has(k)) m.set(k, []); m.get(k).push(f); });
              return Array.from(m.entries()).map(([n, sfs]) => ({ nombre: n, c: contarFranjas(sfs) })).filter((x) => x.c.total > 0)
                .sort((a, b) => (b.c.fuera / b.c.total) - (a.c.fuera / a.c.total) || (b.c.margen / b.c.total) - (a.c.margen / a.c.total));
            })() : [];
            return (
              <div key={nombre}>
                <div onClick={() => setItemAbierto((x) => (x === nombre ? null : nombre))} title="Pulsa para ver su evolución"
                  style={{ display: "grid", gridTemplateColumns: "minmax(90px, 170px) 1fr minmax(120px, auto)", gap: 10, alignItems: "center", cursor: "pointer", padding: "3px 4px", borderRadius: 8, background: abiertoItem ? "#eef2ff" : "transparent" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={nombre}>
                    <span style={{ display: "inline-block", color: "#6366f1", marginRight: 4, transition: "transform .2s", transform: abiertoItem ? "rotate(90deg)" : "none" }}>▸</span>{nombre}
                  </div>
                  <BarraFranjas c={cs} alto={16} />
                  <div style={{ fontSize: 11.5, color: "#64748b", whiteSpace: "nowrap" }}>✅ {cs.dentro} · 🟠 {cs.margen} · 🔴 {cs.fuera}</div>
                </div>
                {abiertoItem && (
                  <div style={{ margin: "6px 0 8px", padding: "10px 12px", background: "#f8fafc", border: "1px solid #e0e7ff", borderRadius: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#475569", marginBottom: 6 }}>📈 Evolución</div>
                    <EvolucionFranjas filas={fs} franja={franjaHorario} franjas={FRANJAS_HORARIO} alto={150} />
                    {subs.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "#475569" }}>👤 Por {actual.sub.texto}</div>
                        {subs.map((x) => (
                          <div key={x.nombre} style={{ display: "grid", gridTemplateColumns: "minmax(90px, 160px) 1fr minmax(120px, auto)", gap: 10, alignItems: "center" }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={x.nombre}>{x.nombre}</div>
                            <BarraFranjas c={x.c} alto={14} />
                            <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>✅ {x.c.dentro} · 🟠 {x.c.margen} · 🔴 {x.c.fuera}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ fontSize: 11, color: "#94a3b8" }}>Ordenado de más a menos envíos fuera de horario. Pulsa en una fila para ver su evolución.</div>
        </div>
      )}
    </div>
  );
};
const RelojDigital = ({ icono, children, title, variante = "neutro" }) => (
  <div title={title} style={{
    display: "inline-flex", alignItems: "center", gap: 4, justifyContent: "center",
    fontFamily: "'Courier New', Consolas, monospace",
    fontWeight: 800, fontSize: 13, padding: "4px 9px", borderRadius: 7,
    letterSpacing: 0.6, whiteSpace: "nowrap",
    ...(RELOJ_ESTILOS[variante] || RELOJ_ESTILOS.neutro),
  }}>
    {icono && <span style={{ fontSize: 11 }}>{icono}</span>}
    {children}
  </div>
);

// Celda con la hora en que se envió el reporte, el tiempo total empleado y el
// tiempo medio por pregunta (calculados en backend a partir de
// "tiempo_segundos" de cada respuesta), cada uno en su propio "reloj digital"
// apilado verticalmente. La hora se pinta en verde si se envió el mismo día
// que le tocaba (f.fecha) y dentro del horario de la asistenta ese día
// (f.hora_entrada/f.hora_salida, reconstruido históricamente en backend), o
// en rojo si se envió otro día o fuera de ese horario.
const CeldaHoraTiempo = ({ f }) => {
  if (!f.enviado) return <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>;
  const hora = formatHora(f.fecha_hora);
  const fechaEnvio = f.fecha_hora ? String(f.fecha_hora).slice(0, 10) : null;
  const diaCorrecto = fechaEnvio && f.fecha ? fechaEnvio === f.fecha : null;
  const franja = franjaHorario(f);

  let variante = "neutro";
  let titulo = "Hora de envío";
  if (diaCorrecto === false) {
    variante = "rojo";
    titulo = "Enviado en un día distinto al que correspondía";
  } else if (franja === "fuera") {
    variante = "rojo";
    titulo = `Enviado fuera del horario de la asistenta (antes de la entrada o más de ${MARGEN_SALIDA_MIN} min después de la salida)`;
  } else if (franja === "margen") {
    variante = "naranja";
    titulo = `Enviado hasta ${MARGEN_SALIDA_MIN} min después de la hora de salida`;
  } else if (diaCorrecto === true) {
    variante = "verde";
    titulo = franja === "dentro" ? "Enviado el día y dentro del horario que correspondía" : "Enviado el día que correspondía";
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
      {hora && (
        <RelojDigital icono="🕒" variante={variante} title={titulo}>
          {hora}
        </RelojDigital>
      )}
      {f.tiempo_total_segundos != null && (
        <RelojDigital icono="⏱" title="Tiempo total / tiempo medio por pregunta">
          {formatDuracion(f.tiempo_total_segundos)}
          {f.tiempo_medio_segundos != null && (
            <span style={{ color: "#64748b", fontWeight: 700 }}>· {f.tiempo_medio_segundos}s</span>
          )}
        </RelojDigital>
      )}
    </div>
  );
};

const VerReporteAsistentas = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const rol = user?.tipo || "";
  const esSupervisora = rol === "supervisora";

  const [periodo, setPeriodo] = useState("hoy");
  const [custom, setCustom] = useState({ desde: todayISO(), hasta: todayISO() });
  const { desde, hasta } = rangoDePeriodo(periodo, custom);

  const [filtroEstado, setFiltroEstado] = useState("todos"); // todos | respondidos | sin_responder
  // Filtros estilo Excel por columna (arrays de valores seleccionados; vacío = sin filtrar)
  const [colFiltros, setColFiltros] = useState({ piso: [], supervisora: [], asistenta: [], ruta: [], preguntas: {}, tiempo: TIEMPO_FILTRO_VACIO });
  const actualizarColFiltro = (campo, valores) => setColFiltros((c) => ({ ...c, [campo]: valores }));
  const actualizarFiltroPregunta = (idPregunta, valores) =>
    setColFiltros((c) => ({ ...c, preguntas: { ...c.preguntas, [idPregunta]: valores } }));

  const hayFiltrosActivos = useMemo(
    () => filtroEstado !== "todos" || colFiltros.piso.length > 0 || colFiltros.supervisora.length > 0
      || colFiltros.asistenta.length > 0 || colFiltros.ruta.length > 0 || Object.values(colFiltros.preguntas).some((v) => v.length > 0)
      || Object.entries(colFiltros.tiempo).some(([k, v]) => (k === "enHorario" ? v !== "todos" : v !== "")),
    [filtroEstado, colFiltros]
  );
  const limpiarFiltros = () => {
    setFiltroEstado("todos");
    setColFiltros({ piso: [], supervisora: [], asistenta: [], ruta: [], preguntas: {}, tiempo: TIEMPO_FILTRO_VACIO });
  };

  const multiDia = desde !== hasta;

  const [todasLasPreguntas, setTodasLasPreguntas] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState("");   // "" = todas las categorías
  const [filasTodas, setFilas] = useState([]);
  // Empresa (CH / CH-SS / CM): filtra TODO (tabla y resumen). Por defecto solo CH.
  const [empresas, setEmpresas] = useState(EMPRESAS_POR_DEFECTO);
  const filas = useMemo(() => filasTodas.filter((f) => f.empresa == null || empresas.includes(f.empresa)), [filasTodas, empresas]);
  const conteoEmpresas = useMemo(() => {
    const m = {};
    const vistos = new Set();
    filasTodas.forEach((f) => { if (f.empresa && !vistos.has(f.id_piso)) { vistos.add(f.id_piso); m[f.empresa] = (m[f.empresa] || 0) + 1; } });
    return m;
  }, [filasTodas]);
  const [opciones, setOpciones] = useState({ supervisoras: [], asistentas: [], pisos: [], supervisoras_inactivas: [], asistentas_inactivas: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showResumen, setShowResumen] = useState(false);
  const [donutsAbiertos, setDonutsAbiertos] = useState(true);
  const [vistaDia, setVistaDia] = useState("linea"); // "linea" | "barras"
  const [supAbierta, setSupAbierta] = useState(null); // supervisora desplegada en "Cumplimiento por supervisora"
  const [filtroSupervisoraRanking, setFiltroSupervisoraRanking] = useState("todas");
  const [filtroSupervisoraHeatmap, setFiltroSupervisoraHeatmap] = useState("todas");

  // Preguntas activas que responde la propia asistenta (a_qn='asistenta'). El desplegable de
  // categoría solo agrupa dentro de este ámbito (el reporte de supervisora será otro panel).
  useEffect(() => {
    axios.get("/api/servicio/reporte_opciones?a_qn=asistenta&solo_activas=1")
      .then((r) => {
        const lista = Array.isArray(r.data) ? r.data : [];
        setTodasLasPreguntas(lista);
      })
      .catch(() => setTodasLasPreguntas([]));
  }, []);

  const categoriasDisponibles = useMemo(() => {
    const vistas = new Set();
    const lista = [];
    todasLasPreguntas.forEach((p) => { if (!vistas.has(p.categoria)) { vistas.add(p.categoria); lista.push(p.categoria); } });
    return lista;
  }, [todasLasPreguntas]);

  const preguntas = useMemo(
    () => (categoriaSeleccionada ? todasLasPreguntas.filter((p) => p.categoria === categoriaSeleccionada) : todasLasPreguntas),
    [todasLasPreguntas, categoriaSeleccionada]
  );

  // Universo de valores para los desplegables de filtro tipo Excel (no depende de la
  // selección actual: el backend ya los devuelve acotados al ámbito del rol)
  const opcionesPiso = useMemo(
    () => (opciones.pisos || []).map((p) => p.abreviatura).filter(Boolean).sort(),
    [opciones.pisos]
  );
  const opcionesSupervisora = useMemo(() => (opciones.supervisoras || []).slice().sort(), [opciones.supervisoras]);
  const opcionesAsistenta = useMemo(
    () => (opciones.asistentas || []).map((a) => a.nombre_asistenta).filter(Boolean).sort(),
    [opciones.asistentas]
  );
  const opcionesRuta = useMemo(() => (opciones.rutas || []).slice().sort((a, b) => a - b), [opciones.rutas]);

  // Supervisoras/asistentas dadas de baja como usuario: se pintan en gris + badge
  // "BAJA" donde aparezcan (filtros, columnas), sin ocultar sus pisos/filas.
  const supervisorasInactivasSet = useMemo(() => new Set(opciones.supervisoras_inactivas || []), [opciones.supervisoras_inactivas]);
  const asistentasInactivasSet = useMemo(() => new Set(opciones.asistentas_inactivas || []), [opciones.asistentas_inactivas]);
  const renderOpcionSupervisora = useCallback((s) => (
    <>
      <span style={supervisorasInactivasSet.has(s) ? { color: "#94a3b8" } : undefined}>{s}</span>
      {supervisorasInactivasSet.has(s) && <span style={st.badgeBajaFiltro}>BAJA</span>}
    </>
  ), [supervisorasInactivasSet]);
  const renderOpcionAsistenta = useCallback((n) => (
    <>
      <span style={asistentasInactivasSet.has(n) ? { color: "#94a3b8" } : undefined}>{n}</span>
      {asistentasInactivasSet.has(n) && <span style={st.badgeBajaFiltro}>BAJA</span>}
    </>
  ), [asistentasInactivasSet]);

  // Valores únicos presentes para una pregunta (para su filtro tipo Excel); incluye "" si hay filas sin responder
  const opcionesPregunta = useCallback((p) => {
    const set = new Set();
    let hayVacios = false;
    filas.forEach((f) => {
      const v = f.respuestas?.[String(p.id)];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) { hayVacios = true; return; }
      if (Array.isArray(v)) v.forEach((x) => set.add(String(x))); // respuesta múltiple
      else set.add(v);
    });
    const arr = Array.from(set);
    if (hayVacios) arr.push("");
    return arr;
  }, [filas]);

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    params.set("fecha_inicio", desde);
    params.set("fecha_fin", hasta);
    axios.get(`/api/reporte-asistentas/panel?${params.toString()}`)
      .then((r) => {
        setFilas(r.data.filas || []);
        setOpciones(r.data.opciones || { supervisoras: [], asistentas: [], pisos: [], supervisoras_inactivas: [], asistentas_inactivas: [] });
      })
      .catch(() => setError("❌ No se pudo cargar el reporte."))
      .finally(() => setLoading(false));
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  // Aplica los filtros de columna (piso/supervisora/asistenta) a cualquier conjunto de filas
  const aplicarColFiltros = useCallback((arr) => arr.filter((f) => {
    if (colFiltros.piso.length && !colFiltros.piso.includes(f.abreviatura)) return false;
    if (colFiltros.supervisora.length && !colFiltros.supervisora.includes(f.user_supervisora)) return false;
    if (colFiltros.asistenta.length && !colFiltros.asistenta.includes(f.nombre_asistenta)
        && !colFiltros.asistenta.includes(f.nombre_responsable)) return false;
    if (colFiltros.ruta.length && !colFiltros.ruta.includes(f.ruta)) return false;
    for (const [idPregunta, seleccionados] of Object.entries(colFiltros.preguntas)) {
      if (!seleccionados.length) continue;
      const v = f.respuestas?.[idPregunta];
      if (Array.isArray(v)) { // respuesta múltiple: vale si alguna de las marcadas está seleccionada
        if (!v.some((x) => seleccionados.includes(String(x)))) return false;
        continue;
      }
      const valorNormalizado = (v === undefined || v === null) ? "" : v;
      if (!seleccionados.includes(valorNormalizado)) return false;
    }
    const t = colFiltros.tiempo;
    if (t.totalMin !== "" && (f.tiempo_total_segundos == null || f.tiempo_total_segundos < Number(t.totalMin) * 60)) return false;
    if (t.totalMax !== "" && (f.tiempo_total_segundos == null || f.tiempo_total_segundos > Number(t.totalMax) * 60)) return false;
    if (t.medioMin !== "" && (f.tiempo_medio_segundos == null || f.tiempo_medio_segundos < Number(t.medioMin))) return false;
    if (t.medioMax !== "" && (f.tiempo_medio_segundos == null || f.tiempo_medio_segundos > Number(t.medioMax))) return false;
    if (t.horaDesde !== "" || t.horaHasta !== "") {
      const horaEnvio = f.fecha_hora ? String(f.fecha_hora).slice(11, 16) : null;
      if (!horaEnvio) return false;
      if (t.horaDesde !== "" && horaEnvio < t.horaDesde) return false;
      if (t.horaHasta !== "" && horaEnvio > t.horaHasta) return false;
    }
    if (t.enHorario !== "todos") {
      const fr = franjaHorario(f);
      if (!fr || fr !== t.enHorario) return false;
    }
    return true;
  }), [colFiltros]);

  // "Respondido" = tiene al menos una respuesta para las preguntas de la categoría
  // seleccionada (no solo que exista un reporte ese día, que puede ser de otra categoría).
  const tieneRespuestaCategoria = useCallback((f) => preguntas.some((p) => {
    const v = f.respuestas?.[String(p.id)];
    return v !== undefined && v !== null && v !== "";
  }), [preguntas]);

  // filasFiltradas: aplica los filtros de columna (independiente del filtro rápido de estado)
  const filasFiltradas = useMemo(() => aplicarColFiltros(filas), [filas, aplicarColFiltros]);

  const filasVisibles = useMemo(() => {
    if (filtroEstado === "respondidos") return filasFiltradas.filter((f) => f.enviado && tieneRespuestaCategoria(f));
    if (filtroEstado === "sin_responder") return filasFiltradas.filter((f) => !f.enviado || !tieneRespuestaCategoria(f));
    return filasFiltradas;
  }, [filasFiltradas, filtroEstado, tieneRespuestaCategoria]);


  // En rango de varios días: agrupar por piso para mostrar una cabecera distinguida
  // por piso y, debajo, una fila por cada día (en vez de repetir el piso en cada fila).
  const gruposPorPiso = useMemo(() => {
    if (!multiDia) return [];
    const orden = [];
    const vistos = new Set();
    filasVisibles.forEach((f) => { if (!vistos.has(f.id_piso)) { vistos.add(f.id_piso); orden.push(f.id_piso); } });
    const porPiso = new Map();
    filasVisibles.forEach((f) => {
      if (!porPiso.has(f.id_piso)) porPiso.set(f.id_piso, []);
      porPiso.get(f.id_piso).push(f);
    });
    return orden.map((idPiso) => {
      const dias = porPiso.get(idPiso).slice().sort((a, b) => a.fecha.localeCompare(b.fecha));
      return { piso: dias[0], dias };
    });
  }, [filasVisibles, multiDia]);
  const carga = useCargaProgresiva(filasVisibles, gruposPorPiso);

  // El resumen es general para el período seleccionado: usa "filas" (sin los filtros de
  // columna ni el filtro rápido de estado, que solo afectan a la tabla principal).
  // Cumplimiento por asistenta: cuántos días de los esperados presentó reporte
  const statsAsistentas = useMemo(() => {
    const mapa = new Map();
    filas.forEach((f) => {
      // por quien TENÍA que ir (la sustituta los días de sustitución, no la titular de baja)
      const key = f.id_responsable || f.id_asistenta || f.nombre_responsable || f.nombre_asistenta || "—";
      if (!mapa.has(key)) mapa.set(key, { nombre: f.nombre_responsable || f.nombre_asistenta || "Sin nombre", supervisora: f.user_supervisora || "—", enviados: 0, total: 0, sustitutaDe: new Set() });
      const e = mapa.get(key);
      if (f.sustitucion && f.sustitucion.nombre_titular) e.sustitutaDe.add(f.sustitucion.nombre_titular);
      e.total++;
      if (f.enviado) e.enviados++;
    });
    return Array.from(mapa.values()).map((e) => ({
      ...e,
      nombre: e.sustitutaDe.size ? `🔄 ${e.nombre} (sustituta de ${Array.from(e.sustitutaDe).join(", ")})` : e.nombre,
      pct: e.total ? Math.round((e.enviados / e.total) * 100) : null,
    }));
  }, [filas]);

  // Opciones de supervisora para el filtro del ranking (solo tiene sentido si hay más de una).
  const opcionesSupervisoraRanking = useMemo(
    () => Array.from(new Set(statsAsistentas.map((e) => e.supervisora))).sort(),
    [statsAsistentas]
  );

  // Si la supervisora seleccionada deja de tener datos en el período (o cambia de sesión),
  // se vuelve a "todas" en vez de dejar el ranking vacío en silencio.
  useEffect(() => {
    if (filtroSupervisoraRanking !== "todas" && !opcionesSupervisoraRanking.includes(filtroSupervisoraRanking)) {
      setFiltroSupervisoraRanking("todas");
    }
  }, [opcionesSupervisoraRanking, filtroSupervisoraRanking]);

  useEffect(() => {
    if (filtroSupervisoraHeatmap !== "todas" && !opcionesSupervisoraRanking.includes(filtroSupervisoraHeatmap)) {
      setFiltroSupervisoraHeatmap("todas");
    }
  }, [opcionesSupervisoraRanking, filtroSupervisoraHeatmap]);

  // Filas del heatmap, filtradas por supervisora (independiente del filtro del
  // ranking: el heatmap se ve en su propio bloque, más abajo en la página).
  const filasHeatmap = useMemo(() => {
    if (filtroSupervisoraHeatmap === "todas") return filas;
    return filas.filter((f) => f.user_supervisora === filtroSupervisoraHeatmap);
  }, [filas, filtroSupervisoraHeatmap]);

  const statsAsistentasParaRanking = useMemo(() => {
    if (filtroSupervisoraRanking === "todas") return statsAsistentas;
    return statsAsistentas.filter((e) => e.supervisora === filtroSupervisoraRanking);
  }, [statsAsistentas, filtroSupervisoraRanking]);

  const todosMasCumplen = useMemo(
    () => statsAsistentasParaRanking.filter((e) => e.total > 0).sort((a, b) => (b.pct - a.pct) || (b.enviados - a.enviados)),
    [statsAsistentasParaRanking]
  );
  const rankingMasCumplen = useMemo(() => todosMasCumplen.slice(0, 5), [todosMasCumplen]);
  const todosMenosCumplen = useMemo(
    () => statsAsistentasParaRanking.filter((e) => e.total > 0).sort((a, b) => (a.pct - b.pct) || (a.enviados - b.enviados)),
    [statsAsistentasParaRanking]
  );
  const rankingMenosCumplen = useMemo(() => todosMenosCumplen.slice(0, 5), [todosMenosCumplen]);

  // Cumplimiento por supervisora
  const statsSupervisoras = useMemo(() => {
    const mapa = new Map();
    filas.forEach((f) => {
      const key = f.user_supervisora || "—";
      if (!mapa.has(key)) mapa.set(key, { nombre: key, enviados: 0, total: 0 });
      const e = mapa.get(key);
      e.total++;
      if (f.enviado) e.enviados++;
    });
    return Array.from(mapa.values())
      .map((e) => ({ ...e, pct: e.total ? Math.round((e.enviados / e.total) * 100) : null }))
      .sort((a, b) => b.total - a.total);
  }, [filas]);

  // Serie diaria de cada supervisora para la mini-tendencia
  const seriesSupervisoras = useMemo(() => {
    const m = new Map();
    filas.forEach((f) => {
      const key = f.user_supervisora || "—";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(f);
    });
    return new Map(Array.from(m.entries()).map(([k, fs]) => [k, statsDias(fs)]));
  }, [filas]);

  // Cumplimiento por día (útil sobre todo con rangos de varios días)
  const statsPorDia = useMemo(() => {
    const mapa = new Map();
    filas.forEach((f) => {
      if (!mapa.has(f.fecha)) mapa.set(f.fecha, { fecha: f.fecha, enviados: 0, total: 0 });
      const e = mapa.get(f.fecha);
      e.total++;
      if (f.enviado) e.enviados++;
    });
    return Array.from(mapa.values())
      .map((e) => ({ ...e, pct: e.total ? Math.round((e.enviados / e.total) * 100) : null }))
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [filas]);

  const totalesGlobales = useMemo(() => {
    const total = filas.length;
    const enviados = filas.filter((f) => f.enviado).length;
    return { total, enviados, sinEnviar: total - enviados, pct: total ? Math.round((enviados / total) * 100) : null };
  }, [filas]);

  // Mini-donuts de cumplimiento por servicio (cabecera), en el orden fijo de
  // ORDEN_SERVICIOS; los servicios sin ningún piso en el ámbito actual no se muestran.
  const totalesPorServicio = useMemo(() => {
    const mapa = new Map();
    filas.forEach((f) => {
      const key = f.servicio;
      if (!key) return;
      if (!mapa.has(key)) mapa.set(key, { total: 0, enviados: 0 });
      const e = mapa.get(key);
      e.total++;
      if (f.enviado) e.enviados++;
    });
    return ORDEN_SERVICIOS
      .map((servicio) => ({ servicio, ...(mapa.get(servicio) || { total: 0, enviados: 0 }) }))
      .filter((e) => e.total > 0);
  }, [filas]);

  const nSinReporte = filasVisibles.filter((f) => !f.enviado).length;

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4f8" }}>
      <div style={{ background: "linear-gradient(135deg,#1e293b,#0f172a)", color: "#fff", padding: "14px 16px 18px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "8px 14px" }}>
          ← Volver
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Reporte diario</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>📋 Reporte de Asistentas</div>
        </div>
      </div>

      {/* Selector de categoría del reporte */}
      <div style={{ padding: "16px 16px 12px", background: "linear-gradient(180deg,#fff,#f8fafc)", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ fontSize: 11.5, fontWeight: 800, color: "#6366f1", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6 }}>
          Categoría del reporte de asistenta
        </div>
        <CategoriaSelector categorias={categoriasDisponibles} value={categoriaSeleccionada} onChange={setCategoriaSeleccionada} />
      </div>

      {/* Filtros de período */}
      <div style={{ display: "flex", gap: 6, padding: "10px 14px", overflowX: "auto", background: "#fff", borderBottom: "1px solid #e2e8f0" }}>
        {PERIODOS_DIA.map((p) => (
          <button key={p.key} onClick={() => setPeriodo(p.key)} style={{
            padding: "7px 14px", borderRadius: 20,
            border: periodo === p.key ? "1.5px solid #1e293b" : "1.5px solid #e2e8f0",
            background: periodo === p.key ? "#1e293b" : "#f8fafc",
            color: periodo === p.key ? "#fff" : "#475569",
            fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
          }}>
            {p.label}
          </button>
        ))}
      </div>
      {periodo === "custom" && (
        <div style={{ display: "flex", gap: 10, padding: "8px 16px", alignItems: "center", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Desde</label>
          <input type="date" value={custom.desde} max={custom.hasta}
            onChange={(e) => setCustom((c) => ({ ...c, desde: e.target.value }))}
            style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
          <label style={{ fontSize: 13, fontWeight: 600 }}>Hasta</label>
          <input type="date" value={custom.hasta} min={custom.desde} max={todayISO()}
            onChange={(e) => setCustom((c) => ({ ...c, hasta: e.target.value }))}
            style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
        </div>
      )}

      {/* Cumplimiento (quesitos): debajo de los filtros de período, se cierra al cambiar de período */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0" }}>
        <button
          onClick={() => setDonutsAbiertos((v) => !v)}
          style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "none", border: "none", cursor: "pointer" }}
        >
          <span style={{ fontSize: 12.5, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5 }}>📊 Cumplimiento</span>
          <span style={{ fontSize: 13, color: "#94a3b8", transform: donutsAbiertos ? "rotate(180deg)" : "none", transition: "transform .2s" }}>▾</span>
        </button>
        {donutsAbiertos && (
          <div style={{ padding: "6px 20px 26px", display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <DonutNumeros enviados={totalesGlobales.enviados} total={totalesGlobales.total} loading={loading} size={104} />
              <span style={{ fontSize: 13, color: "#64748b", fontWeight: 700 }}>han hecho el formulario</span>
            </div>
            {!loading && totalesPorServicio.length > 0 && (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 20, flexWrap: "wrap", paddingLeft: 20, borderLeft: "1px solid #e2e8f0" }}>
                {totalesPorServicio.map((e) => (
                  <div key={e.servicio} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                    <DonutNumeros enviados={e.enviados} total={e.total} size={66} compacto />
                    <span style={{ fontSize: 11.5, fontWeight: 800, color: "#475569", letterSpacing: 0.3 }}>{e.servicio}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filtros de selección */}
      <div style={{ display: "flex", gap: 10, padding: "12px 14px", flexWrap: "wrap", background: "#fff", borderBottom: "1px solid #e2e8f0", alignItems: "center" }}>
        <SelectorEmpresas seleccion={empresas} onChange={setEmpresas} conteo={conteoEmpresas} />
        <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}
          style={{
            padding: "12px 18px", borderRadius: 14, border: "2px solid #c7d2fe", fontSize: 15,
            background: "linear-gradient(135deg,#fff,#eef2ff)", fontWeight: 800, color: "#3730a3",
            cursor: "pointer", boxShadow: "0 3px 10px rgba(79,70,229,0.15)", outline: "none",
          }}>
          <option value="todos">📋 Todos</option>
          <option value="respondidos">✅ Solo respondidos</option>
          <option value="sin_responder">⬜ Solo sin responder</option>
        </select>
        {hayFiltrosActivos && (
          <button onClick={limpiarFiltros} style={{
            padding: "9px 14px", borderRadius: 10, border: "1.5px solid #dc2626",
            background: "#fee2e2", color: "#991b1b", fontWeight: 700, fontSize: 12.5, cursor: "pointer",
          }}>
            ✕ Quitar todos los filtros
          </button>
        )}
        <button onClick={() => setShowResumen((s) => !s)} style={{
          padding: "10px 18px", borderRadius: 12, border: "none", cursor: "pointer",
          background: showResumen ? "linear-gradient(135deg,#312e81,#1e1b4b)" : "linear-gradient(135deg,#6366f1,#4338ca)",
          color: "#fff", fontWeight: 800, fontSize: 13.5, letterSpacing: 0.2,
          display: "flex", alignItems: "center", gap: 7,
          boxShadow: showResumen ? "0 4px 14px rgba(30,27,75,0.4)" : "0 4px 14px rgba(99,102,241,0.4)",
          transition: "box-shadow .15s, background .15s",
        }}>
          <span style={{ fontSize: 16 }}>{showResumen ? "📋" : "📊"}</span>
          {showResumen ? "Ver tabla" : "Ver resumen"}
        </button>
      </div>

      {/* Panel de resumen: KPIs + cumplimiento + rankings + heatmap + evolución */}
      {showResumen && (
        <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <KpiCard icon="📋" label="Filas en el período" value={totalesGlobales.total} bg="#eef2ff" color="#4338ca" />
            <KpiCard icon="✅" label="Reportes enviados" value={totalesGlobales.enviados} bg={BG.verde} color={C.verde} />
            <KpiCard icon="⬜" label="Sin enviar" value={totalesGlobales.sinEnviar} bg={BG.rojo} color={C.rojo} />
            <KpiCard icon="📊" label="% Cumplimiento" value={totalesGlobales.pct != null ? `${totalesGlobales.pct}%` : "—"} bg={BG.azul} color={C.azul} />
          </div>
          <AtencionSinReportes filasSemana={filas} loading={loading} error={error} mostrarAsistenta={true} vistaAsistentas />

          {statsPorDia.length > 1 && (
            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                <h3 style={{ ...st.sh, margin: 0 }}>{vistaDia === "linea" ? "📈 Evolución del cumplimiento" : "📅 Cumplimiento por día"}</h3>
                <ToggleVista valor={vistaDia} onChange={setVistaDia} opciones={[{ k: "linea", label: "📈 Línea" }, { k: "barras", label: "📊 Barras" }]} />
              </div>
              {vistaDia === "linea" ? (
                <EvolucionCumplimiento dias={statsPorDia} />
              ) : statsPorDia.map((d) => (
                <BarraCumplimiento key={d.fecha}
                  label={new Date(d.fecha).toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "short" })}
                  enviados={d.enviados} total={d.total} pct={d.pct}
                />
              ))}
            </div>
          )}

          {statsPorDia.length > 1 && (
            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
              <h3 style={st.sh}>🗓️ Calendario de cumplimiento</h3>
              <CalendarioCumplimiento dias={statsPorDia} />
            </div>
          )}

          {!esSupervisora && statsSupervisoras.length > 1 && (
            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
              <h3 style={st.sh}>👩‍💼 Cumplimiento por supervisora</h3>
              {statsSupervisoras.map((s) => {
                const pulsable = statsPorDia.length > 1;
                const abierta = pulsable && supAbierta === s.nombre;
                return (
                  <div key={s.nombre}>
                    <div onClick={pulsable ? () => setSupAbierta((x) => (x === s.nombre ? null : s.nombre)) : undefined} title={pulsable ? "Pulsa para ver su evolución" : undefined}
                      style={{ display: "flex", alignItems: "center", gap: 10, cursor: pulsable ? "pointer" : "default", padding: "4px 6px 0", borderRadius: 10, background: abierta ? "#eef2ff" : "transparent" }}>
                      {pulsable && <span style={{ color: "#6366f1", fontSize: 13, transition: "transform .2s", transform: abierta ? "rotate(90deg)" : "none" }}>▸</span>}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <BarraCumplimiento label={s.nombre} enviados={s.enviados} total={s.total} pct={s.pct} />
                      </div>
                      <MiniTendencia serie={seriesSupervisoras.get(s.nombre) || []} media={statsPorDia} />
                    </div>
                    {abierta && <DetalleSupervisora dias={seriesSupervisoras.get(s.nombre) || []} media={statsPorDia} filasSup={filas.filter((f) => (f.user_supervisora || "—") === s.nombre)} subValor={nombreAsistenta} Barra={BarraCumplimiento} />}
                  </div>
                );
              })}
              {statsPorDia.length > 1 && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Pulsa en una supervisora para ver su evolución y la de sus asistentas.</div>}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {opcionesSupervisoraRanking.length > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: "#64748b" }}>👩‍💼 Rankings de:</span>
                <select value={filtroSupervisoraRanking} onChange={(e) => setFiltroSupervisoraRanking(e.target.value)}
                  style={{
                    padding: "8px 14px", borderRadius: 12, border: "2px solid #c7d2fe", fontSize: 13,
                    background: "linear-gradient(135deg,#fff,#eef2ff)", fontWeight: 800, color: "#3730a3",
                    cursor: "pointer", boxShadow: "0 2px 8px rgba(79,70,229,0.15)", outline: "none",
                  }}>
                  <option value="todas">Todas las supervisoras</option>
                  {opcionesSupervisoraRanking.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <RankingLista titulo="Las que más cumplen" emoji="🌟" items={rankingMasCumplen} todos={todosMasCumplen} vacio="Sin datos suficientes."
                mostrarSupervisora={opcionesSupervisoraRanking.length > 1 && filtroSupervisoraRanking === "todas"}
                render={(r) => <span style={{ fontSize: 13, fontWeight: 800, ...colorPorPct(r.pct), borderRadius: 10, padding: "3px 10px" }}>{r.enviados}/{r.total} · {r.pct}%</span>} />
              <RankingLista titulo="Las que menos cumplen" emoji="⚠️" items={rankingMenosCumplen} todos={todosMenosCumplen} vacio="Sin datos suficientes."
                mostrarSupervisora={opcionesSupervisoraRanking.length > 1 && filtroSupervisoraRanking === "todas"}
                render={(r) => <span style={{ fontSize: 13, fontWeight: 800, ...colorPorPct(r.pct), borderRadius: 10, padding: "3px 10px" }}>{r.enviados}/{r.total} · {r.pct}%</span>} />
            </div>
          </div>

          <RespetoHorarios filas={filas} agrupaciones={AGRUPACIONES_RESPETO} />

          <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
            <h3 style={{ ...st.sh, marginBottom: 8 }}>🌡️ Heatmap por pregunta <span style={{ fontWeight: 500, color: "#94a3b8", fontSize: 12 }}>({filasHeatmap.length} filas del período seleccionado)</span></h3>
            {!esSupervisora && opcionesSupervisoraRanking.length > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: "#64748b" }}>👩‍💼 Supervisora:</span>
                <select value={filtroSupervisoraHeatmap} onChange={(e) => setFiltroSupervisoraHeatmap(e.target.value)}
                  style={{
                    padding: "8px 14px", borderRadius: 12, border: "2px solid #c7d2fe", fontSize: 13,
                    background: "linear-gradient(135deg,#fff,#eef2ff)", fontWeight: 800, color: "#3730a3",
                    cursor: "pointer", boxShadow: "0 2px 8px rgba(79,70,229,0.15)", outline: "none",
                  }}>
                  <option value="todas">Todas las supervisoras</option>
                  {opcionesSupervisoraRanking.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            )}
            {preguntas.length === 0 ? (
              <div style={{ color: "#94a3b8", fontSize: 13 }}>No hay preguntas activas en esta categoría.</div>
            ) : preguntas.map((p) => <HeatmapPregunta key={p.id} pregunta={p} filas={filasHeatmap} />)}
          </div>

          {preguntas.length > 0 && (
            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
              <h3 style={{ ...st.sh, marginBottom: 8 }}>📈 Evolución de las preguntas <span style={{ fontWeight: 500, color: "#94a3b8", fontSize: 12 }}>(% de respuestas en verde)</span></h3>
              <EvolucionPreguntas preguntas={preguntas} filas={filas} contar={contarPregunta} />
            </div>
          )}
        </div>
      )}

      {/* Tabla principal (oculta mientras se ve el resumen) */}
      {!showResumen && (
      <div style={{ padding: "12px 14px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 50, color: "#64748b" }}>⏳ Cargando…</div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: 30, color: C.rojo }}>{error}</div>
        ) : filasVisibles.length === 0 ? (
          <div style={{ textAlign: "center", padding: 50, color: "#94a3b8" }}>No hay pisos con reporte esperado en este período.</div>
        ) : (
          <>
            <div style={{ marginBottom: 10, fontSize: 13, fontWeight: 700, color: "#334155" }}>
              Mostrando {filasVisibles.length} fila{filasVisibles.length !== 1 ? "s" : ""}
            </div>
            {nSinReporte > 0 && (
              <div style={{ marginBottom: 10, fontSize: 12.5, color: "#64748b", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ ...st.badge, background: "#94a3b8" }}>⬜</span> {nSinReporte} pisos sin reporte enviado ese día
              </div>
            )}
            <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", boxShadow: "0 2px 10px rgba(15,23,42,0.05)" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
                <thead>
                  <tr>
                    {multiDia ? (
                      <>
                        <th style={{ ...st.th, minWidth: 90, left: 0, zIndex: 4 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span title="Filtrar por piso" style={{ display: "inline-flex", alignItems: "center" }}>
                              🏠<ColumnFilterPopover opciones={opcionesPiso} seleccionados={colFiltros.piso} onChange={(v) => actualizarColFiltro("piso", v)} />
                            </span>
                            {!esSupervisora && (
                              <span title="Filtrar por supervisora" style={{ display: "inline-flex", alignItems: "center" }}>
                                👩‍💼<ColumnFilterPopover opciones={opcionesSupervisora} seleccionados={colFiltros.supervisora} onChange={(v) => actualizarColFiltro("supervisora", v)} renderOption={renderOpcionSupervisora} />
                              </span>
                            )}
                            <span title="Filtrar por ruta" style={{ display: "inline-flex", alignItems: "center" }}>
                              🚦<ColumnFilterPopover opciones={opcionesRuta} seleccionados={colFiltros.ruta} onChange={(v) => actualizarColFiltro("ruta", v)} renderLabel={(v) => RUTA_LABEL[v] || String(v)} />
                            </span>
                          </div>
                        </th>
                        <th style={{ ...st.th, minWidth: 160 }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <span>Asistenta</span>
                            <ColumnFilterPopover opciones={opcionesAsistenta} seleccionados={colFiltros.asistenta} onChange={(v) => actualizarColFiltro("asistenta", v)} renderOption={renderOpcionAsistenta} />
                          </div>
                        </th>
                      </>
                    ) : (
                      <>
                        <th style={{ ...st.th, minWidth: 140, left: 0, zIndex: 4 }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <span>Piso</span>
                            <ColumnFilterPopover opciones={opcionesPiso} seleccionados={colFiltros.piso} onChange={(v) => actualizarColFiltro("piso", v)} />
                          </div>
                        </th>
                        <th style={st.th}>Servicio</th>
                        <th style={st.th}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <span>Ruta</span>
                            <ColumnFilterPopover opciones={opcionesRuta} seleccionados={colFiltros.ruta} onChange={(v) => actualizarColFiltro("ruta", v)} renderLabel={(v) => RUTA_LABEL[v] || String(v)} />
                          </div>
                        </th>
                        {!esSupervisora && (
                          <th style={st.th}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <span>Supervisora</span>
                              <ColumnFilterPopover opciones={opcionesSupervisora} seleccionados={colFiltros.supervisora} onChange={(v) => actualizarColFiltro("supervisora", v)} renderOption={renderOpcionSupervisora} />
                            </div>
                          </th>
                        )}
                        <th style={st.th}>ID Asist.</th>
                        <th style={st.th}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <span>Asistenta</span>
                            <ColumnFilterPopover opciones={opcionesAsistenta} seleccionados={colFiltros.asistenta} onChange={(v) => actualizarColFiltro("asistenta", v)} renderOption={renderOpcionAsistenta} />
                          </div>
                        </th>
                      </>
                    )}
                    <th style={{ ...st.th, minWidth: 150, textAlign: "center", zIndex: 5 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
                        <span>🕒 Hora / Tiempo medio</span>
                        <RangoTiempoPopover filtro={colFiltros.tiempo} onChange={(v) => actualizarColFiltro("tiempo", v)} conEnHorario />
                      </div>
                    </th>
                    {preguntas.map((p) => (
                      <th key={p.id} style={{ ...st.th, minWidth: 140, textAlign: "center" }}>
                        <div style={{ fontSize: 20 }}>{p.emoji}</div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                          <span style={{ fontSize: 14, fontWeight: 900, color: "#1e293b", whiteSpace: "normal", textTransform: "none", letterSpacing: 0.1, marginTop: 2 }}>{p.subcategoria}</span>
                          <ColumnFilterPopover
                            opciones={opcionesPregunta(p)}
                            seleccionados={colFiltros.preguntas[p.id] || []}
                            onChange={(v) => actualizarFiltroPregunta(p.id, v)}
                            renderLabel={(val) => etiquetaValorPregunta(p, val)}
                          />
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!multiDia ? (
                    carga.filasMostradas.map((f, i) => {
                      const bgFila = !f.enviado ? "#f8fafc" : (i % 2 ? "#fff" : "#fcfdfe");
                      return (
                      <tr key={f.id_piso} style={{ background: bgFila }}>
                        <td style={{ ...st.tdPiso, background: bgFila }}>
                          <div style={{ fontWeight: 800, color: "#1e293b" }}>{f.abreviatura}</div>
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>#{f.id_piso}</div>
                        </td>
                        <td style={st.td}>
                          {f.servicio && (() => {
                            const sc = getServicioStyle(f.servicio);
                            return <span style={{ ...st.badgeServicio, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{f.servicio}</span>;
                          })()}
                        </td>
                        <td style={st.td}>
                          <span style={{ ...st.badgeRuta, background: RUTA_COLOR[f.ruta] || "#94a3b8" }}>{RUTA_LABEL[f.ruta] || "—"}</span>
                        </td>
                        {!esSupervisora && <td style={st.td}>{renderOpcionSupervisora(f.user_supervisora)}</td>}
                        <td style={st.td}>{f.sustitucion ? f.sustitucion.id_sustituta : f.id_asistenta}</td>
                        <td style={st.td}>
                          {f.sustitucion
                            ? <span style={st.badgeSust}>🔄 {f.sustitucion.nombre_sustituta}</span>
                            : <div>{renderOpcionAsistenta(f.nombre_asistenta)}</div>}
                          <InfoSustitucion f={f} />
                          {(f.hora_entrada || f.hora_salida) && (
                            <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 600, marginTop: 1 }}>
                              🕐 {f.hora_entrada || "?"}–{f.hora_salida || "?"}
                            </div>
                          )}
                        </td>
                        <td style={{ ...st.td, textAlign: "center" }}>
                          <CeldaHoraTiempo f={f} />
                        </td>
                        {!f.enviado ? (
                          <td colSpan={preguntas.length} style={{ ...st.td, textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>
                            ⬜ Sin reporte
                          </td>
                        ) : (
                          preguntas.map((p) => (
                            <td key={p.id} style={{ ...st.td, textAlign: "center" }}>
                              <CeldaRespuesta pregunta={p} valor={f.respuestas?.[String(p.id)]} />
                            </td>
                          ))
                        )}
                      </tr>
                      );
                    })
                  ) : (
                    carga.gruposMostrados.map((g) => (
                      <React.Fragment key={g.piso.id_piso}>
                        <tr>
                          <td colSpan={3 + preguntas.length} style={st.grupoHeaderCell}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 20, fontWeight: 900, color: "#312e81", display: "flex", alignItems: "center", gap: 8 }}>
                                🏠 {g.piso.abreviatura}
                                <span style={st.chipId}>#{g.piso.id_piso}</span>
                              </span>
                              {g.piso.servicio && (() => {
                                const sc = getServicioStyle(g.piso.servicio);
                                return <span style={{ ...st.badgeServicio, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{g.piso.servicio}</span>;
                              })()}
                              <span style={{ ...st.badgeRuta, background: RUTA_COLOR[g.piso.ruta] || "#94a3b8" }}>{RUTA_LABEL[g.piso.ruta] || "—"}</span>
                              {!esSupervisora && <span style={st.chipSupervisora}>👩‍💼 {renderOpcionSupervisora(g.piso.user_supervisora)}</span>}
                              <span style={st.chipAsistenta}>🧹 {renderOpcionAsistenta(g.piso.nombre_asistenta)} <span style={{ opacity: 0.75 }}>#{g.piso.id_asistenta}</span></span>
                              <span style={{ ...st.chipDias, marginLeft: "auto" }}>📅 {g.dias.length} día{g.dias.length !== 1 ? "s" : ""}</span>
                            </div>
                          </td>
                        </tr>
                        {g.dias.map((f) => {
                          const bgFila = !f.enviado ? "#f8fafc" : "#fff";
                          return (
                          <tr key={f.fecha} style={{ background: bgFila }}>
                            <td style={{ ...st.tdFecha, background: bgFila }}>
                              {new Date(f.fecha).toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "2-digit" })}
                            </td>
                            <td style={st.td}>
                              {!f.enviado ? (
                                f.sustitucion
                                  ? <span style={{ color: "#b91c1c", fontWeight: 700, fontSize: 12.5 }}>Sin reporte</span>
                                  : <span style={{ color: "#cbd5e1" }}>—</span>
                              ) : f.sustituta ? (
                                <span style={st.badgeSustituta} title="La envió otra persona (ni la que tocaba ese día ni la titular)">⚠️ {f.nombre_reporte}</span>
                              ) : f.sustitucion ? (
                                <span style={st.badgeSust} title="Sustitución aplicada">🔄 {f.nombre_reporte || f.sustitucion.nombre_sustituta}</span>
                              ) : (
                                <span>{f.nombre_reporte || f.nombre_asistenta}</span>
                              )}
                              <InfoSustitucion f={f} />
                              {(f.hora_entrada || f.hora_salida) && (
                                <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 600, marginTop: 1 }}>
                                  🕐 {f.hora_entrada || "?"}–{f.hora_salida || "?"}
                                </div>
                              )}
                            </td>
                            <td style={{ ...st.td, textAlign: "center" }}>
                              <CeldaHoraTiempo f={f} />
                            </td>
                            {!f.enviado ? (
                              <td colSpan={preguntas.length} style={{ ...st.td, textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>
                                ⬜ Sin reporte
                              </td>
                            ) : (
                              preguntas.map((p) => (
                                <td key={p.id} style={{ ...st.td, textAlign: "center" }}>
                                  <CeldaRespuesta pregunta={p} valor={f.respuestas?.[String(p.id)]} />
                                </td>
                              ))
                            )}
                          </tr>
                          );
                        })}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <MasFilas {...carga} />
          </>
        )}
      </div>
      )}
    </div>
  );
};

const st = {
  sh: { margin: "0 0 10px", fontSize: 14, fontWeight: 800, color: "#334155" },
  th: { padding: "9px 12px", fontSize: 11.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1.5px solid #e2e8f0", whiteSpace: "nowrap", textAlign: "left", background: "#f8fafc", position: "sticky", top: 0, zIndex: 2 },
  td: { padding: "8px 12px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap", color: "#334155" },
  tdPiso: { padding: "8px 12px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap", position: "sticky", left: 0, zIndex: 1 },
  tdFecha: { padding: "8px 12px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap", color: "#475569", fontWeight: 700, fontSize: 12, textTransform: "capitalize", position: "sticky", left: 0, zIndex: 1 },
  grupoHeaderCell: { padding: "16px 18px", background: "linear-gradient(135deg,#eef2ff,#e0e7ff)", borderTop: "3px solid #818cf8", borderBottom: "1.5px solid #c7d2fe", color: "#312e81", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)" },
  chipId: { fontSize: 12, fontWeight: 700, color: "#4338ca", background: "#fff", borderRadius: 8, padding: "2px 9px" },
  chipSupervisora: { fontSize: 13, fontWeight: 700, color: "#0369a1", background: "#e0f2fe", borderRadius: 20, padding: "5px 12px" },
  chipAsistenta: { fontSize: 13, fontWeight: 700, color: "#a16207", background: "#fef9c3", borderRadius: 20, padding: "5px 12px" },
  chipDias: { fontSize: 12.5, fontWeight: 800, color: "#4338ca", background: "#fff", border: "1.5px solid #c7d2fe", borderRadius: 20, padding: "5px 12px" },
  badge: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4, minWidth: 26, height: 24, padding: "0 8px", borderRadius: 12, fontSize: 12, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" },
  badgeSust: { display: "inline-flex", alignItems: "center", gap: 4, background: "#f3e8ff", color: "#6b21a8", border: "1.5px solid #d8b4fe", borderRadius: 10, padding: "3px 9px", fontSize: 12, fontWeight: 800, whiteSpace: "nowrap" },
  badgeSustituta: { display: "inline-flex", alignItems: "center", gap: 4, background: "#ffedd5", color: "#c2410c", border: "1.5px solid #fdba74", borderRadius: 10, padding: "3px 9px", fontSize: 12, fontWeight: 800, whiteSpace: "nowrap" },
  badgeBajaFiltro: { display: "inline-block", marginLeft: 6, padding: "1px 7px", fontSize: 10.5, fontWeight: 800, color: "#fff", backgroundColor: "#e74c3c", borderRadius: 8, verticalAlign: "middle" },
  badgeServicio: { display: "inline-block", padding: "3px 10px", borderRadius: 10, fontSize: 12, fontWeight: 800, letterSpacing: 0.2, whiteSpace: "nowrap" },
  badgeRuta: { display: "inline-block", padding: "3px 10px", borderRadius: 10, fontSize: 12, fontWeight: 800, letterSpacing: 0.2, whiteSpace: "nowrap", color: "#fff" },
};

export default VerReporteAsistentas;
